import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LanguageCode, LANGUAGES_LIST } from '@/data/languages';
import { Globe } from 'lucide-react';
import { useMemo } from 'react';
import { useLanguageSafe } from '../context/LanguageContext';

export type TranslationData = {
  [key in LanguageCode]?: string;
};

interface TranslationProps {
  value?: TranslationData;
  title?: string;
  description?: string;
  onChange: (value: TranslationData) => void;
  required?: boolean;
  onFocus?: (id: string, value: string) => void;
  id: string;
}

/**
 * Enhanced translation input component with better visual styling
 */
function Translation({
  value = {},
  title,
  description,
  onChange,
  required = false,
  onFocus,
  id,
}: TranslationProps) {
  const languageContext = useLanguageSafe();
  const selectedLang = languageContext?.selectedLanguage || 'en';

  const langData = useMemo(() => LANGUAGES_LIST.find((l) => l.code === selectedLang), [selectedLang]);

  const handleTranslationChange = (translation: string) => {
    onChange({
      ...value,
      [selectedLang]: translation,
    });
  };

  if (!langData) return null;

  const hasValue = value[selectedLang] && value[selectedLang].trim().length > 0;

  return (
    <div key={`translation-${selectedLang}`} className="space-y-2.5">
      {title && (
        <div className="space-y-1">
          <Label className="flex items-center gap-1.5 text-sm font-medium">
            <Globe className="text-muted-foreground h-3.5 w-3.5" />
            {title}
            {required && <span className="text-destructive text-xs">*</span>}
            <span className="bg-muted text-muted-foreground ml-1 rounded-md px-1.5 py-0.5 text-xs font-normal">
              {langData.name}
            </span>
          </Label>
          {description && <p className="text-muted-foreground text-xs leading-relaxed">{description}</p>}
        </div>
      )}
      <div className="relative">
        <Input
          onFocus={(e) => onFocus?.(id, e.target.value)}
          id={id}
          key={`input-${selectedLang}`}
          value={value[selectedLang] || ''}
          onChange={(e) => handleTranslationChange(e.target.value)}
          placeholder={`Enter ${langData.name} translation...`}
          className={`w-full transition-all ${hasValue ? 'border-primary/50 bg-primary/5' : ''}`}
          required={required}
        />
        {hasValue && (
          <div className="bg-primary absolute right-2 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full" />
        )}
      </div>
    </div>
  );
}

export default Translation;
